import React, { FC, useRef, useEffect } from 'react'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import { useId } from '@reach/auto-id'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import { max } from 'ramda'
import { useRefWithUpdate } from 'hooks/useRefWithUpdate'

am4core.useTheme(am4themes_animated)

export type PyramidSeries = {
  colors: string[]
  labels: string[]
  values: number[][][]
}

type PyramidChartProps = {
  data: PyramidSeries
  enableTooltips?: boolean
  selected?: string[]
  onSelect?: (index: string[]) => void
}

export const PyramidChart: FC<PyramidChartProps> = ({ data, selected = [], onSelect }) => {
  const chartId = useId()
  const chartRef = useRef<am4core.Container>()
  const femaleRef = useRef<am4charts.XYChart>()
  const maleRef = useRef<am4charts.XYChart>()
  const filters = useRefWithUpdate<string[]>(selected)

  const handleSelection = (value: string) => {
    if (!onSelect) return
    const isSelected = filters.current.includes(value)

    const newSelected = isSelected
      ? filters.current.filter((filter) => value !== filter)
      : [...filters.current, value]

    const allSelected = newSelected.length === data.labels.length
    allSelected ? onSelect([]) : onSelect(newSelected)
  }

  const transformedData = data.labels.reduce((total: any[], label, index) => {
    const obj: any = {
      label,
      opacity: filters.current.includes(label) ? 0.3 : 1,
    }

    data.values.forEach((valueArr, series) => {
      obj[`males${series}`] = valueArr[0][index]
      obj[`females${series}`] = valueArr[1][index]
    })

    total.push(obj)
    return total
  }, [])

  const maxValue = data.values.flat(2).reduce(max)

  useEffect(() => {
    const mainContainer = am4core.create(chartId, am4core.Container)
    mainContainer.width = am4core.percent(100)
    mainContainer.height = am4core.percent(100)
    mainContainer.layout = 'horizontal'

    const maleChart = mainContainer.createChild(am4charts.XYChart)
    maleChart.paddingRight = 0
    maleChart.data = transformedData

    const femaleChart = mainContainer.createChild(am4charts.XYChart)
    femaleChart.paddingLeft = 0
    femaleChart.data = transformedData

    createChartAxes(maleChart, maxValue)
    createChartAxes(femaleChart, maxValue, true)

    data.values.forEach((_v, index) => {
      // Create series
      const maleSeries = maleChart.series.push(new am4charts.ColumnSeries())
      maleSeries.dataFields.valueX = `males${index}`
      maleSeries.dataFields.categoryY = 'label'
      maleSeries.interpolationDuration = 1000
      maleSeries.columns.template.tooltipText = "Males, {categoryY}: {valueX.formatNumber('#.00')}%"
      maleSeries.strokeWidth = 0
      maleSeries.fill = am4core.color(data.colors[0])

      if (maleSeries.tooltip) {
        maleSeries.tooltip.getFillFromObject = false
        maleSeries.tooltip.background.fill = am4core.color('#FFF')
        maleSeries.tooltip.background.fillOpacity = 1
        maleSeries.tooltip.label.fill = am4core.color('#777')
      }
      maleSeries.columns.template.events.on(
        'hit',
        function (ev) {
          handleSelection(ev.target.dataItem?.categories.categoryY || '')
        },
        mainContainer,
      )

      maleSeries.events.on('ready', function (ev) {
        ev.target.columns.each((column) => {
          column.fillOpacity =
            column.dataItem && filters.current.includes(column.dataItem.categories.categoryY)
              ? 0.3
              : 1
        })
      })

      // Create series
      const femaleSeries = femaleChart.series.push(new am4charts.ColumnSeries())
      femaleSeries.dataFields.valueX = `females${index}`
      femaleSeries.dataFields.categoryY = 'label'
      femaleSeries.interpolationDuration = 1000
      femaleSeries.columns.template.tooltipText =
        "Females, {categoryY}: {valueX.formatNumber('#.00')}%"
      femaleSeries.strokeWidth = 0
      femaleSeries.fill = am4core.color(data.colors[1])

      if (femaleSeries.tooltip) {
        femaleSeries.tooltip.getFillFromObject = false
        femaleSeries.tooltip.background.fill = am4core.color('#FFF')
        femaleSeries.tooltip.background.fillOpacity = 1
        femaleSeries.tooltip.label.fill = am4core.color('#777')
      }
      femaleSeries.columns.template.events.on(
        'hit',
        function (ev) {
          handleSelection(ev.target.dataItem?.categories.categoryY || '')
        },
        mainContainer,
      )

      femaleSeries.events.on('ready', function (ev) {
        ev.target.columns.each((column) => {
          column.fillOpacity =
            column.dataItem && filters.current.includes(column.dataItem.categories.categoryY)
              ? 0.3
              : 1
        })
      })
    })

    // Remove ugly amCharts icon xD
    // This chart draws 3 watermarks, because we render a container and two charts
    mainContainer.events.on('validated', () => {
      document.querySelectorAll(`g[aria-labelledby^="id-"`).forEach((element) => element.remove())
    })

    chartRef.current = mainContainer
    maleRef.current = maleChart
    femaleRef.current = femaleChart

    return () => {
      chartRef.current?.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, `lenght-${data.labels.length}`])

  // Update selection
  useEffect(() => {
    if (!maleRef.current || !femaleRef.current) return

    maleRef.current.series.each((serie) => {
      ;(serie as am4charts.ColumnSeries).columns.values.forEach((column) => {
        const opacity =
          column.dataItem && selected.includes(column.dataItem.categories.categoryY) ? 0.3 : 1
        column.fillOpacity = opacity
      })
    })

    femaleRef.current.series.each((serie) => {
      ;(serie as am4charts.ColumnSeries).columns.values.forEach((column) => {
        const opacity =
          column.dataItem && selected.includes(column.dataItem.categories.categoryY) ? 0.3 : 1
        column.fillOpacity = opacity
      })
    })
  }, [selected, maleRef, femaleRef])

  useEffect(() => {
    if (!maleRef.current) return
    if (!femaleRef.current) return

    transformedData.forEach((_i, index) => {
      const maleValues = maleRef.current!.data[index]
      const femaleValues = femaleRef.current!.data[index]

      Object.keys(maleValues).forEach((key: any) => {
        maleValues[key] = transformedData[index][key]
      })

      Object.keys(femaleValues).forEach((key: any) => {
        femaleValues[key] = transformedData[index][key]
      })
    })
    //[index]
    maleRef.current.invalidateRawData()
    femaleRef.current.invalidateRawData()
  }, [transformedData, selected])

  return <div id={chartId} style={{ width: '100%', height: '100%' }}></div>
}

const createChartAxes = (chart: am4charts.XYChart, maxValue: number, isFemale?: boolean) => {
  const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
  categoryAxis.dataFields.category = 'label'
  categoryAxis.renderer.minGridDistance = 15
  categoryAxis.renderer.cellStartLocation = 0.1
  categoryAxis.renderer.cellEndLocation = 0.9
  categoryAxis.renderer.grid.template.opacity = 0
  categoryAxis.renderer.fontSize = 12
  if (isFemale) {
    categoryAxis.renderer.axis.hide()
    categoryAxis.renderer.opposite = true
  }

  const valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
  valueAxis.min = 0
  valueAxis.max = maxValue
  valueAxis.renderer.inversed = true
  valueAxis.numberFormatter = new am4core.NumberFormatter()
  valueAxis.numberFormatter.numberFormat = "#.#'%'"
  valueAxis.renderer.minGridDistance = 30
  valueAxis.renderer.fontSize = 12
  valueAxis.height = 12
  if (isFemale) {
    valueAxis.renderer.inversed = false
  }
}
